import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  Container,
  Stepper,
  Step,
  Typography,
  StepButton,
  Alert,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Box,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { OpenInNew, QrCode } from '@mui/icons-material'
import { Asset, AssetGroup, Aurora, AuroraAssetGroup, Project } from '../../types'
import { AssetGroupType } from '../../enums'
import { getProject } from '../../services/projectService'
import { getAurora } from '../../services/aurora'
import CodeInput from '../../components/CodeInput'
import { createErrorNotification } from '../../redux/slices/notifications'
import InstallationCompletedStep from '../../components/installation/InstallationCompletedStep'
import QrReaderButton from '../../components/QrReaderButton'
import AuroraDetailsList from '../../components/AuroraDetailsList'
import LoadingState from '../../components/LoadingState'
import AssetGroupSetupStep from '../../components/installation/AssetGroupSetupStep'
import SelectProjectStep from '../../components/installation/SelectProjectStep'
import AuroraBlockDisplay from '../../components/AuroraBlockDisplay'
import AuroraStackPositionSelect from '../AuroraStackPositionSelect'
import { getAssetGroup } from '../../services/assetGroup'
import { isAuroraAssetGroup } from '../../utils/aurora'
import { set } from 'date-fns'
import AuroraSetupStep from './AuroraSetupStep'

export enum AuroraInstallationStepKey {
  SELECT_PROJECT = 'select_project',
  AURORA_SETUP = 'aurora_setup',
  SELECT_AURORA_POSITION = 'select_aurora_position',
  ASSET_SETUP = 'asset_setup',
  INSTALLATION_COMPLETED = 'installation_completed'
}

export type AuroraInstallationStep = {
  key: AuroraInstallationStepKey
  title: string
}

type Position = {
  x: number
  y: number
}

export interface AuroraInstallationProps {
  step?: string
  assetGroupId?: string
  projectId?: number
  auroraId?: string
  levelId?: number
  liftId?: number
  skipSelectProject?: boolean
  skipAuroraSetup?: boolean
  onProjectChange?: (project: Project | null) => void
  onAuroraChange?: (aurora: Aurora | null) => void
  onAssetGroupChange?: (assetGroup: AssetGroup | null) => void
  onStepChange?: (step: string) => void
  onCancel?: () => void
}

export default function AuroraInstallation({
  step: initialStep,
  assetGroupId: initialAssetGroupId,
  projectId: initialProjectId,
  auroraId: initialAuroraId,
  levelId,
  liftId,
  skipSelectProject = false,
  skipAuroraSetup = false,
  onProjectChange,
  onAuroraChange,
  onAssetGroupChange,
  onStepChange,
  onCancel,
  ...props
}: AuroraInstallationProps) {
  const { t } = useTranslation('installation')

  const navigate = useNavigate()

  const theme = useTheme()

  const steps = useMemo(() => {
    const steps: AuroraInstallationStep[] = []

    if (!skipSelectProject) {
      steps.push({
        key: AuroraInstallationStepKey.SELECT_PROJECT,
        title: t('selectProject')
      })
    }

    if (!skipAuroraSetup) {
      steps.push({
        key: AuroraInstallationStepKey.AURORA_SETUP,
        title: t('readQrCodeOrEnterCode')
      })
    }

    steps.push({
      key: AuroraInstallationStepKey.SELECT_AURORA_POSITION,
      title: t('selectUnitPosition')
    })

    steps.push({
      key: AuroraInstallationStepKey.ASSET_SETUP,
      title: t('enterAssetGroupDetails')
    })

    steps.push({
      key: AuroraInstallationStepKey.INSTALLATION_COMPLETED,
      title: t('installationCompleted')
    })

    return steps
  }, [skipSelectProject, skipAuroraSetup, t])

  const getStepIndex = (step: AuroraInstallationStep) => {
    return steps.findIndex((s) => s.key === step.key)
  }

  const [activeStepIndex, setActiveStepIndex] = useState<number>(() => {
    const index = steps.findIndex((step) => step.key === initialStep)
    return index !== -1 ? index : 0
  })

  const [projectId, setProjectId] = useState<number | null>(initialProjectId ?? null)

  const [project, setProject] = useState<Project | null>(null)

  const [aurora, setAurora] = useState<Aurora | null>(null)

  const [position, setPosition] = useState<Position | null>(null)

  const [asset, setAsset] = useState<Asset | null>(null)

  const [assetGroupId, setAssetGroupId] = useState<string | null>(initialAssetGroupId ?? null)

  const [assetGroup, setAssetGroup] = useState<AuroraAssetGroup | null>(null)

  const isVerticalStepper = useMediaQuery(theme.breakpoints.down(1920))

  const activeStepKey = steps[activeStepIndex].key

  const clampStepIndex = (step: number) => {
    if (step < 0) {
      return 0
    }

    if (step > steps.length - 1) {
      return steps.length - 1
    }

    return step
  }

  const isActiveStep = (step: AuroraInstallationStep) => {
    const index = getStepIndex(step)

    if (index === -1) {
      return false
    }

    return activeStepIndex === index
  }

  const isStepCompleted = (step: AuroraInstallationStep) => {
    const index = getStepIndex(step)

    if (index === -1) {
      return false
    }

    return activeStepIndex === steps.length - 1 || activeStepIndex > index
  }

  const loadProject = async (projectId: number) => {
    try {
      const project = await getProject(projectId)
      setProject(project)
      return project
    } catch {
      setProject(null)
      return null
    }
  }

  const loadAurora = async (auroraId: string) => {
    try {
      setAurora(await getAurora(auroraId))
    } catch {
      setAurora(null)
    }
  }

  const loadAssetGroup = async (assetGroupId: string) => {
    try {
      const assetGroup = await getAssetGroup(assetGroupId)
      if (isAuroraAssetGroup(assetGroup)) {
        setAssetGroup(assetGroup)
      } else {
        setAssetGroup(null)
      }
    } catch {
      setAssetGroup(null)
    }
  }

  const handleNext = () => {
    setActiveStepIndex((prevActiveStepIndex) => clampStepIndex(prevActiveStepIndex + 1))
  }

  const handleBack = () => {
    setActiveStepIndex((prevActiveStepIndex) => clampStepIndex(prevActiveStepIndex - 1))
  }

  const handleCancel = () => {
    onCancel?.()
  }

  const handleProjectChange = async (projectId: number | undefined) => {
    setProjectId(projectId ?? null)
  }

  const handleAuroraChange = (aurora: Aurora | null) => {
    setAurora(aurora)
    onAuroraChange?.(aurora)
  }

  const handleAssetGroupChange = (assetGroup: AssetGroup | null) => {
    if (isAuroraAssetGroup(assetGroup) || assetGroup === null) {
      setAssetGroup(assetGroup)
      setAssetGroupId(assetGroup?.id ?? null)
      // onAssetGroupChange?.(assetGroup)
    }
  }

  const handlePositionsChange = (positions: Record<string, Position>) => {
    setAssetGroup((prev) => {
      if (prev === null) {
        return prev
      }

      return {
        ...prev,
        customData: {
          ...prev?.customData,
          positions
        }
      }
    })
  }

  useEffect(() => {
    if (projectId !== null) {
      ;(async () => {
        const project = await loadProject(projectId)
        onProjectChange?.(project)
      })()
    } else {
      setProject(null)
      onProjectChange?.(null)
    }
  }, [projectId])

  useEffect(() => {
    if (initialAuroraId) {
      loadAurora(initialAuroraId)
    } else {
      setAurora(null)
    }
  }, [initialAuroraId])

  useEffect(() => {
    if (initialAssetGroupId) {
      loadAssetGroup(initialAssetGroupId)
    } else {
      setAssetGroup(null)
    }
  }, [initialAssetGroupId])

  useEffect(() => {
    onStepChange?.(activeStepKey)
  }, [activeStepKey])

  return (
    <Container maxWidth={false} sx={{ p: '0 !important' }}>
      {steps.length > 1 && (
        <Stepper
          nonLinear={true}
          activeStep={activeStepIndex}
          orientation={isVerticalStepper ? 'vertical' : 'horizontal'}
          sx={{ mt: 8, mb: 10 }}
        >
          {steps.map((step, index) => (
            <Step index={index} key={step.key} completed={isStepCompleted(step)}>
              <StepButton>{step.title}</StepButton>
            </Step>
          ))}
        </Stepper>
      )}

      <Container maxWidth="lg" sx={{ p: '0 !important' }}>
        {project === null || activeStepKey === AuroraInstallationStepKey.SELECT_PROJECT ? (
          <SelectProjectStep
            value={projectId ?? null}
            onChange={handleProjectChange}
            onNext={() => {
              if (projectId !== null) {
                handleNext()
              }
            }}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        ) : aurora === null || activeStepKey === AuroraInstallationStepKey.AURORA_SETUP ? (
          <AuroraSetupStep
            project={project}
            aurora={aurora}
            onAuroraChange={handleAuroraChange}
            onNext={() => {
              if (aurora !== null) {
                handleNext()
              }
            }}
            onBack={handleBack}
          />
        ) : position === null ||
          activeStepKey === AuroraInstallationStepKey.SELECT_AURORA_POSITION ? (
          <SelectAuroraPositionStep
            aurora={aurora}
            assetGroup={assetGroup}
            position={position}
            onPositionChange={setPosition}
            onPositionsChange={handlePositionsChange}
            onNext={() => {
              if (position !== null) {
                handleNext()
              }
            }}
            onBack={handleBack}
          />
        ) : assetGroup === null || activeStepKey === AuroraInstallationStepKey.ASSET_SETUP ? (
          <AssetGroupSetupStep
            type={AssetGroupType.AURORA}
            assetGroup={assetGroup ?? undefined}
            project={project}
            aurora={aurora}
            position={position}
            liftId={liftId}
            levelId={levelId}
            onAuroraChange={setAurora}
            onAssetGroupChange={handleAssetGroupChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        ) : assetGroup !== null &&
          activeStepKey === AuroraInstallationStepKey.INSTALLATION_COMPLETED ? (
          <InstallationCompletedStep project={project} aurora={aurora} assetGroup={assetGroup} />
        ) : null}
      </Container>
    </Container>
  )
}

interface SelectAuroraPositionStepProps {
  assetGroup: AuroraAssetGroup | null
  aurora: Aurora
  position: Position | null
  onPositionChange: (position: Position | null) => void
  onPositionsChange?: (positions: Record<string, Position>) => void
  onNext: () => void
  onBack: () => void
}

function SelectAuroraPositionStep({
  assetGroup,
  aurora,
  position,
  onPositionChange,
  onPositionsChange,
  onNext,
  onBack
}: SelectAuroraPositionStepProps) {
  const { t } = useTranslation('installation')

  const handleBack = () => {
    onPositionChange(null)
    onBack()
  }

  return (
    <Card>
      <CardHeader title={t('selectUnitPosition')} sx={{ textAlign: 'center', pt: 6 }} />
      <CardContent sx={{ pt: 2 }}>
        <AuroraStackPositionSelect
          assetGroup={assetGroup ?? undefined}
          aurora={aurora}
          position={position}
          onPositionChange={onPositionChange}
          onPositionsChange={onPositionsChange}
        />
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="outlined" color="primary" onClick={handleBack}>
          {t('back')}
        </Button>
        <Button variant="contained" color="primary" disabled={position === null} onClick={onNext}>
          {t('next')}
        </Button>
      </CardActions>
    </Card>
  )
}
